import './scrolltable.scss';

class Scrolltable {
    constructor (element, options) {
        const defaults = {
            offset: 4,
            scrollNavigation: true
        };

        this.settings = Object.assign({}, defaults, options);

        this.scrollable = false;

        this.scrollStart = true;
        this.scrollEnd = false;

        this.currentColumn = 0;

        // element can be a bare <table> (RTE builds the wrapper) or a
        // pre-rendered [data-scrolltable="root"] container whose markup
        // already exists in the DOM (datasheet templates)
        if (element.tagName === 'TABLE') {
            this.$table = element;
            this.buildWrapper(() => {
                this.initialize();
            });
        } else {
            this.$scrollTable = element;
            this.cacheWrapper(() => {
                this.initialize();
            });
        }
    }

    initialize () {
        this.checkScrolltable();
        this.setEvents();
    }

    buildWrapper (callback) {
        const _createElementWithClass = (tag, classname) => {
            const $object = document.createElement(tag);
            $object.classList.add(classname);
            return $object;
        };

        this.$tableCaption = this.$table.querySelector('caption');

        this.$scrollTable = _createElementWithClass('div', 'scrolltable');
        this.$table.parentNode.insertBefore(this.$scrollTable, this.$table);

        this.$scrollTableInner = _createElementWithClass('div', 'scrolltable__inner');
        this.$scrollTable.appendChild(this.$scrollTableInner);

        this.$scrollTableScroller = _createElementWithClass('div', 'scrolltable__scroller');
        this.$scrollTableInner.appendChild(this.$scrollTableScroller);

        this.$scrollTableScrollerInner = _createElementWithClass('div', 'scrolltable__scroller-inner');
        this.$scrollTableScroller.appendChild(this.$scrollTableScrollerInner);

        this.$scrollTableScrollerInner.appendChild(this.$table);

        this.$scrollTableControls = _createElementWithClass('div', 'scrolltable__controls');
        this.$scrollTable.appendChild(this.$scrollTableControls);

        this.$scrollTableProgressbar = _createElementWithClass('div', 'scrolltable__progressbar');
        this.$scrollTableControls.appendChild(this.$scrollTableProgressbar);

        this.$scrollTableProgressbarIndicator = _createElementWithClass('div', 'scrolltable__progressbar-indicator');
        this.$scrollTableProgressbar.appendChild(this.$scrollTableProgressbarIndicator);

        this.$scrollTableButtons = _createElementWithClass('div', 'scrolltable__buttons');
        this.$scrollTableControls.appendChild(this.$scrollTableButtons);

        this.$scrollTablePrevButton = _createElementWithClass('a', 'scrolltable__prev');
        this.$scrollTableButtons.appendChild(this.$scrollTablePrevButton);

        this.$scrollTableNextButton = _createElementWithClass('a', 'scrolltable__next');
        this.$scrollTableButtons.appendChild(this.$scrollTableNextButton);

        this.$tableFirstRow = this.$table.querySelectorAll('tr')[0];
        this.$tableFirstColumns = this.$tableFirstRow.querySelectorAll('th, td');

        if (typeof callback === 'function') {
            callback();
        }
    }

    cacheWrapper (callback) {
        // wrapper markup is already rendered server-side, only cache references.
        // The PDF export renders through Gotenberg (headless Chromium) WITH
        // JavaScript, so gate the grow/scroll enhancement on the print flag:
        // body.print (set for print=1) keeps the static 100% column layout for
        // the PDF, while the interactive screen view scrolls (EOSM-284)
        const enhanced = !document.body.classList.contains('print');
        if (enhanced) {
            this.$scrollTable.classList.add('scrolltable--enhanced');
        }

        this.$table = this.$scrollTable.querySelector('table');
        this.$tableCaption = this.$scrollTable.querySelector('caption');

        this.$scrollTableInner = this.$scrollTable.querySelector('.scrolltable__inner');
        this.$scrollTableScroller = this.$scrollTable.querySelector('.scrolltable__scroller');
        this.$scrollTableScrollerInner = this.$scrollTable.querySelector('.scrolltable__scroller-inner');

        this.$scrollTableControls = this.$scrollTable.querySelector('.scrolltable__controls');
        this.$scrollTableProgressbar = this.$scrollTable.querySelector('.scrolltable__progressbar');
        this.$scrollTableProgressbarIndicator = this.$scrollTable.querySelector('.scrolltable__progressbar-indicator');
        this.$scrollTableButtons = this.$scrollTable.querySelector('.scrolltable__buttons');
        this.$scrollTablePrevButton = this.$scrollTable.querySelector('.scrolltable__prev');
        this.$scrollTableNextButton = this.$scrollTable.querySelector('.scrolltable__next');

        if (this.$table) {
            this.$tableFirstRow = this.$table.querySelectorAll('tr')[0];

            if (this.$tableFirstRow) {
                this.$tableFirstColumns = this.$tableFirstRow.querySelectorAll('th, td');
            }
        }

        if (typeof callback === 'function') {
            callback();
        }
    }

    checkScrolltable () {
        // detect overflow via the scroller's own scroll metrics: robust for both
        // the RTE-built wrapper and the pre-rendered datasheet markup, and it does
        // not depend on the inner being pre-sized to the table.
        // Never enable the scroll UI in the PDF export (rendered with body.print by
        // Gotenberg, which runs JS): a scrollbar and prev/next controls make no
        // sense in a static PDF.
        const isPrint = document.body.classList.contains('print');

        if (!isPrint && this.$scrollTableScroller.scrollWidth > this.$scrollTableScroller.clientWidth) {
            this.$scrollTable.classList.add('scrolltable--scroll');
        } else {
            this.$scrollTable.classList.remove('scrolltable--scroll');
        }

        this.checkButtons();
    }

    setEvents () {
        this.$scrollTableScroller.addEventListener('scroll', () => {
            this.onScroll(this.$scrollTableScroller.scrollLeft);
        });

        this.$scrollTablePrevButton.addEventListener('click', () => {
            if (this.scrollStart === false) {
                let column = 0;
                if (this.currentColumn > 0) {
                    column = this.currentColumn - 1;
                }
                this.jumpToColumn(column);
            }
        });

        this.$scrollTableNextButton.addEventListener('click', () => {
            if (this.scrollEnd === false) {
                let column = this.$tableFirstColumns.length;
                if (this.currentColumn < this.$tableFirstColumns.length) {
                    column = this.currentColumn + 1;
                }
                this.jumpToColumn(column);
            }
        });

        window.addEventListener('resize', () => {
            this.checkScrolltable();
        });

        window.addEventListener('orientationchange', () => {
            this.checkScrolltable();
        });
    }

    onScroll (scrollLeft) {
        const scrollDistance = Math.round(this.$scrollTableScroller.scrollWidth - this.$scrollTableScroller.clientWidth);
        let percentage = scrollDistance > 0 ? Math.round(100 / scrollDistance * scrollLeft) : 0;

        if (percentage >= 100) {
            percentage = 100;
            this.scrollEnd = true;
            this.scrollStart = false;
        } else if (percentage === 0) {
            this.scrollStart = true;
            this.scrollEnd = false;
        } else {
            this.scrollStart = false;
            this.scrollEnd = false;
        }

        this.setProgress(percentage, scrollLeft);
        this.setCurrentColumn(scrollLeft);
        this.checkButtons();

        if (this.$tableCaption) {
            this.setCaption(scrollLeft);
        }
    }

    setProgress (percentage) {
        const moveDistance = Math.round(this.$scrollTableProgressbar.offsetWidth - this.$scrollTableProgressbarIndicator.offsetWidth);
        const moveStep = Math.round(moveDistance / 100 * percentage);

        this.$scrollTableProgressbarIndicator.style.transform = 'translateX(' + moveStep + 'px)';
    }

    setCaption (scrollLeft) {
        this.$tableCaption.style.transform = 'translateX(' + scrollLeft + 'px)';
    }

    checkButtons () {
        if (this.scrollStart === true) {
            this.$scrollTablePrevButton.classList.add('is--inactive');
        } else {
            this.$scrollTablePrevButton.classList.remove('is--inactive');
        }

        if (this.scrollEnd === true) {
            this.$scrollTableNextButton.classList.add('is--inactive');
        } else {
            this.$scrollTableNextButton.classList.remove('is--inactive');
        }
    }

    setCurrentColumn (scrollLeft) {
        for (let i = 0; i < this.$tableFirstColumns.length - 1; i++) {
            const $currentColumn = this.$tableFirstColumns[i];
            const $nextColumn = this.$tableFirstColumns[i + 1];

            const currentOffset = parseInt($currentColumn.offsetLeft);
            const nextOffset = parseInt($nextColumn.offsetLeft);

            if (this.currentColumn !== i && currentOffset <= scrollLeft && scrollLeft < nextOffset) {
                this.currentColumn = i;
            }
        }
    }

    jumpToColumn (index) {
        const scrollPosition = this.$tableFirstColumns[index].offsetLeft - this.$table.offsetLeft;
        this.$scrollTableScroller.scrollTo({
            top: 0,
            left: scrollPosition,
            behavior: 'smooth'
        });
        this.currentColumn = index;
    }
}

export { Scrolltable };

// initialize pre-rendered scrolltables (datasheet templates) on content load
window.addEventListener('content.loaded', (e) => {
    const $context = e.detail.$context;

    if (!$context) {
        return;
    }

    const $$scrolltables = $context.querySelectorAll('[data-scrolltable="root"]');

    for (let i = 0; i < $$scrolltables.length; i++) {
        const $scrolltable = $$scrolltables[i];

        // guard against double initialization
        if ($scrolltable.API) {
            continue;
        }

        if ($scrolltable.querySelector('table')) {
            $scrolltable.API = new Scrolltable($scrolltable);
        }
    }
});
